import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useIsSearchToFeedEnabled } from '@modules/common/hooks';
import { ArtefactType } from '@modules/common/types';
import { getArtefactTypeLabel } from '@modules/common/utils';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { EscHotKeyIcon, ReturnHotKeyIcon } from '../HotKeyIcons';
import { useNotificationContext } from '../Notification/NotificationContext';

interface ArchiveArtefactConfirmDialogProps {
  onArchive: () => Promise<void>;
  artefactTitle: string;
  artefactType: ArtefactType;
}

export const ArchiveArtefactConfirmDialog = ({
  onArchive,
  artefactTitle,
  artefactType,
}: ArchiveArtefactConfirmDialogProps) => {
  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const { closeModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();
  const [isLoading, setIsLoading] = useState(false);

  const onClose = () => closeModal();

  const artefactTypeLabel = getArtefactTypeLabel(
    artefactType,
    isSearchToFeedEnabled,
  );

  const archiveArtefact = async () => {
    setIsLoading(true);
    try {
      await onArchive();
    } catch {
      showErrorNotification({
        contentComponent: `Failed to archive this ${artefactTypeLabel}`,
        props: { isClosable: true },
      });
    }
    onClose();
  };

  return (
    <>
      <ModalHeader title={`Archive ${artefactTypeLabel}?`} />
      <Divider />
      <DialogContent>
        <Typography variant="body1">
          Do you want to archive "{artefactTitle}"?
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Stack direction="row" m={1} spacing={2}>
          <Button
            onClick={onClose}
            variant="outlined"
            endIcon={<EscHotKeyIcon />}
          >
            No, keep it
          </Button>
          <LoadingButton
            loading={isLoading}
            onClick={archiveArtefact}
            variant="contained"
            color="error"
            autoFocus
            endIcon={<ReturnHotKeyIcon backgroundColor="error" />}
          >
            Yes, Archive it
          </LoadingButton>
        </Stack>
      </DialogActions>
    </>
  );
};
